import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { id, isoDateTime, requiredText, shortText } from '../fields.ts';

export const LIST_KINDS = ['grocery', 'shopping', 'packing', 'todo', 'general'] as const;

export const lists = defineCollection({
  name: 'lists',
  module: 'lists',
  label: { one: 'list', other: 'lists' },
  schema: z.object({
    name: requiredText(100),
    kind: z.enum(LIST_KINDS).default('general'),
    /** Short emoji shown beside the name. */
    icon: shortText(16).default(''),
    archived: z.boolean().default(false),
  }),
  defaultAudience: 'family',
  childAccess: 'contribute',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: [],
});

export const listItems = defineCollection({
  name: 'list_items',
  module: 'lists',
  label: { one: 'list item', other: 'list items' },
  schema: z.object({
    listId: id,
    text: requiredText(300),
    quantity: shortText(40).default(''),
    note: shortText(500).default(''),
    /** Free-form aisle/section, e.g. "Produce". */
    category: shortText(40).default(''),
    checked: z.boolean().default(false),
    checkedBy: id.nullable().default(null),
    checkedAt: isoDateTime.nullable().default(null),
    /** Fractional index for manual ordering. */
    sortKey: z.string().min(1).max(64),
    /** Where the item came from, e.g. a planned meal. */
    source: z
      .object({ kind: z.literal('meal'), id, title: shortText(200) })
      .nullable()
      .default(null),
  }),
  parent: { kind: 'fixed', collection: 'lists', field: 'listId' },
  defaultAudience: 'family',
  childAccess: 'contribute',
  editPolicy: 'collaborative',
  immutableFields: ['listId', 'source'],
  manualMergeFields: [],
});

export type List = RecordOf<typeof lists>;
export type ListItem = RecordOf<typeof listItems>;
